export type DeleteUserIssue = {
  code: string;
  message?: string;
  entityIds?: string[];
  amount?: number;
};

export type DeleteUserErrorBody = {
  error?: string;
  warnings?: DeleteUserIssue[];
  blockers?: DeleteUserIssue[];
  canDelete?: boolean;
  blocked?: boolean;
};

export type DeleteUserBlockedDialogContent = {
  primaryHtml: string;
  secondaryText: string;
};

const DELETE_USER_ISSUE_MESSAGES: Record<string, string> = {
  paid_upcoming_participation:
    'Cancel your paid upcoming activity registration before deleting your account',
  paid_upcoming_organizer_activity:
    'Cancel your paid upcoming organizer activity and resolve funds before deleting this account',
  organizer_earnings_waiting_for_payment:
    'Wait for completed paid activity earnings to become payable before deleting this account',
  organizer_wallet_balance_remaining:
    'Withdraw all remaining wallet balance before deleting this account',
  wallet_balance_remaining:
    'Withdraw your remaining wallet balance before deleting your account',
  free_upcoming_organizer_activity:
    'Cancel upcoming free organizer activities and notify participants before deleting this account',
};

const DELETE_USER_BLOCKED_DIALOG_CONTENT: Record<
  string,
  (issue: DeleteUserIssue) => DeleteUserBlockedDialogContent
> = {
  organizer_wallet_balance_remaining: (issue) => ({
    primaryHtml: `This account has a remaining balance of <strong class="delete-user-blocked__amount">${formatWalletAmount(issue.amount)}</strong>`,
    secondaryText:
      'Please contact the user to clear the remaining balance before deleting the account.',
  }),
  organizer_earnings_waiting_for_payment: (issue) => ({
    primaryHtml: issue.amount
      ? `This account has earnings of <strong class="delete-user-blocked__amount">${formatWalletAmount(issue.amount)}</strong> waiting for payment`
      : 'This account has completed paid activity earnings waiting for payment',
    secondaryText:
      'Please contact the user to wait for completed paid activity earnings to become payable before deleting the account.',
  }),
  paid_upcoming_participation: () => ({
    primaryHtml: 'This account has a paid upcoming activity registration',
    secondaryText:
      'Please contact the user to cancel their paid upcoming activity registration before deleting the account.',
  }),
  paid_upcoming_organizer_activity: () => ({
    primaryHtml: 'This account has a paid upcoming organizer activity',
    secondaryText:
      'Please contact the user to cancel their paid upcoming organizer activity and resolve funds before deleting the account.',
  }),
  wallet_balance_remaining: (issue) => ({
    primaryHtml: `This account has a remaining balance of <strong class="delete-user-blocked__amount">${formatWalletAmount(issue.amount)}</strong>`,
    secondaryText:
      'Please contact the user to clear the remaining wallet balance before deleting the account.',
  }),
  free_upcoming_organizer_activity: () => ({
    primaryHtml: 'This account has upcoming free organizer activities',
    secondaryText:
      'Please contact the user to cancel upcoming free organizer activities and notify participants before deleting the account.',
  }),
};

export function formatWalletAmount(amount?: number): string {
  const cents = typeof amount === 'number' ? amount : 0;
  return `${(cents / 100).toFixed(2)}€`;
}

export function isDeleteUserBlocked(
  eligibility: Pick<DeleteUserErrorBody, 'blockers' | 'blocked'>,
): boolean {
  return !!eligibility.blocked || (eligibility.blockers?.length ?? 0) > 0;
}

export function getDeleteUserIssueMessage(code: string): string {
  return DELETE_USER_ISSUE_MESSAGES[code] ?? code;
}

export function getDeleteUserIssueMessages(
  blockers: DeleteUserIssue[] = [],
  warnings: DeleteUserIssue[] = [],
): string {
  const messages = [...blockers, ...warnings]
    .map((issue) => getDeleteUserIssueMessage(issue.code))
    .filter(Boolean);
  return [...new Set(messages)].join(' ');
}

export function buildDeleteUserBlockedDialogContent(
  blockers: DeleteUserIssue[] = [],
): DeleteUserBlockedDialogContent {
  const blocker = blockers[0];
  if (!blocker) {
    return {
      primaryHtml: 'This account cannot be deleted right now.',
      secondaryText: 'Please resolve the blocking conditions before trying again.',
    };
  }

  const builder = DELETE_USER_BLOCKED_DIALOG_CONTENT[blocker.code];
  if (builder) {
    return builder(blocker);
  }

  return {
    primaryHtml: getDeleteUserIssueMessage(blocker.code),
    secondaryText: blocker.message ?? 'Please resolve the blocking conditions before trying again.',
  };
}

export function getDeleteUserErrorMessage(
  error: unknown,
  fallback = 'Failed to delete user',
): string {
  const body = extractDeleteUserErrorBody(error);
  if (body) {
    const issueMessage = getDeleteUserIssueMessages(
      body.blockers ?? [],
      body.warnings ?? [],
    );
    if (issueMessage) {
      return issueMessage;
    }
  }
  return getGenericDeleteUserErrorMessage(error, fallback);
}

export function extractDeleteUserErrorBody(
  error: unknown,
): DeleteUserErrorBody | null {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const payload = (error as { error?: unknown }).error;
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const body = payload as DeleteUserErrorBody;
  if (body.warnings?.length || body.blockers?.length || body.error) {
    return body;
  }
  return null;
}

function getGenericDeleteUserErrorMessage(
  error: unknown,
  fallback: string,
): string {
  const status = (error as { status?: number })?.status;
  if (status === 404) {
    return 'User account not found';
  }
  return fallback;
}
